import { computed, effect, signal } from "@preact/signals-react";
import { filterBy, orderBy } from "@progress/kendo-data-query";
import { GridColumn } from "@progress/kendo-react-grid";
import { PersonWithSuspense } from "../../Common/MicrosoftGraph/MgtPerson";
import { userEntries, userTemplates } from "../../Common/Store/CommonSignals";

export const selectedTemplate = signal();
export const selectedTemplateStructure = computed(() => {
  if (!selectedTemplate.value) return null;
  return userTemplates.value?.find((template) => template.id === selectedTemplate.value.id);
});
export const showDeleteEntryDialog = signal(false);
export const showRevokeUrlDialog = signal(false);
export const showSplitEntryDialog = signal(false);
export const showSplitEntryWindow = signal(false);
export const showEditSplitEntryWindow = signal(false);
export const selectedEntry = signal();
export const currentTemplateId = signal();
export const currentEntryId = signal();
export const viewEntryGridFilter = signal(getEntryGridFilters());
function getEntryGridFilters() {
  const filterData = localStorage.getItem("ENTRY_GRID_FILTERS");
  return JSON.tryParse(filterData, {});
}
//Saves the template data to local storage when it changes
effect(() => {
  if (!viewEntryGridFilter.value) {
    localStorage.setItem("ENTRY_GRID_FILTERS", "{}");
  } else {
    localStorage.setItem("ENTRY_GRID_FILTERS", JSON.stringify(viewEntryGridFilter.value));
  }
});

//#region TemplateSelector Signals
export const templateOptionsFilter = signal([]);
export const templateOptions = computed(() => {
  if (!userTemplates.value) return [];
  return filterBy(
    orderBy(
      userTemplates.value?.map((template) => {
        return {
          label: template.templateName,
          branch: template.branch,
          id: template.id,
        };
      }),
      [
        { field: "branch", dir: "asc" },
        { field: "label", dir: "asc" },
      ]
    ),
    templateOptionsFilter.value
  );
});
//#endregion TemplateSelector Signals

//#region Entry Grid Signals
export const entryGridPageSettings = signal({
  skip: 0,
  take: 10,
  buttonCount: 5,
  info: true,
  pagerType: "numeric",
  pageSizes: [5, 10, 20],
  previousNext: true,
});
export const entryGridFilter = signal([]);
export const entryGridSort = signal([]);
export const entryGridData = computed(() => {
  if (!selectedTemplate.value) return null;
  if (!userEntries.value?.[selectedTemplate.value.id]) return null;

  const templateEntries = userEntries.value[selectedTemplate.value.id];

  return filterBy(
    orderBy(
      templateEntries.map((entry) => {
        const flattenedFields = {};

        entry.sections.forEach((section) => {
          section.fields.forEach((field) => {
            flattenedFields[field.fieldName] = field;
          });
        });

        const newEntry = { ...entry, ...flattenedFields };

        delete newEntry.sections;
        delete newEntry.id;
        delete newEntry.templateId;
        delete newEntry._ts;
        if (!newEntry.actionId) delete newEntry.actionId;
        delete newEntry.createdDate;

        return newEntry;
      }),
      entryGridSort.value
    ),
    entryGridFilter.value
  );
});

export const entryGridColumns = computed(() => {
  if (!entryGridData.value || entryGridData.value.length === 0) return [];

  // Extract column definitions
  const firstEntry = entryGridData.value[0];
  const columns = [];

  Object.keys(firstEntry).forEach((key) => {
    const field = firstEntry[key];
    if (typeof field === "object" && field !== null && field.fieldName) {
      if (!field?.properties?.IsDetail) {
        if (field.fieldType?.includes("PeoplePicker")) {
          columns.push(
            <GridColumn
              field={`${field.fieldName}.value`}
              title={field.fieldName}
              key={field.fieldName}
              cell={() => {
                try {
                  const personObject = JSON.tryParse(field.value, []);
                  return personObject[0].id ? (
                    <td>
                      {personObject.map((person) => {
                        return (
                          <div
                            key={person.id}
                            title={person.displayName}
                          >
                            <PersonWithSuspense
                              key={person.id}
                              userId={person.id}
                              person={person}
                            />
                          </div>
                        );
                      })}
                    </td>
                  ) : (
                    <td></td>
                  );
                } catch (e) {
                  console.error(e);
                  return <td></td>;
                }
              }}
            />
          );
        } else {
          columns.push(
            <GridColumn
              field={`${field.fieldName}.value`}
              title={field.fieldName}
              key={field.fieldName}
            />
          );
        }
      }
    }
  });

  return columns;
});
//#endregion Entry Grid Signals
